import React, { useState, useRef, useMemo } from 'react';
import { AlertBlock, CustomHeroButton, Typography, CustomHeroInput } from '../../components';
import { Button, Card, CardBody } from '@heroui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import { clientAuthService } from '../../api';
import { EditBlueRoundedIc, InfoRedIc, NoAvatarBigIc } from '../../components/icons';
import { calculatePasswordStrength, getPasswordStrengthColor } from '../../utils/password';
import { Icon } from '@iconify/react/dist/iconify.js';
import { showCreatorSuccess, showError } from '../../utils';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

const createSignUpSchemaPatched = (userType: 'agency' | 'creator' | 'marketer', t: any) => {
  const fieldName = userType === 'agency' ? t('settings.agencyName') : t('settings.name');
  const fieldMinMessage = t('settings.minSymbols');
  const fieldRequiredMessage = t('settings.required');
  const baseSchema: any = {
    name: Yup.string()
      .min(2, `${fieldName} ${fieldMinMessage}`)
      .required(`${fieldName} ${fieldRequiredMessage}`),
    tg_tag: Yup.string().min(2, t('settings.telegramMin')).required(t('settings.telegramRequired')),
    whats_app: Yup.string().min(2, t('settings.whatsappMin')).max(25, t('settings.whatsappMax')),
  };

  return Yup.object().shape(baseSchema);
};

const changePasswordSchema = (t: any) =>
  Yup.object({
    password: Yup.string()
      .min(4, t('settings.passwordRequirements'))
      .max(25, t('settings.passwordRequirements'))
      .matches(/[A-Z]/, t('settings.passwordRequirements'))
      .matches(/[0-9]/, t('settings.passwordRequirements'))
      .required(t('settings.passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('settings.passwordsMatch'))
      .required(t('settings.confirmPasswordRequired')),
  });

const Settings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslations();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard'), href: '/' },
      { label: t('sidebar.settings'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  // Avatar upload states
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle avatar file selection
  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setAvatarError(t('settings.fileNotSelected'));
      return;
    }

    // Reset previous errors
    setAvatarError(null);

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      setAvatarError(t('settings.allowedFileTypes'));
      return;
    }

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setAvatarError(t('settings.fileTooLarge'));
      return;
    }

    // Set selected file and create preview
    setSelectedAvatar(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Cancel avatar selection
  const handleCancelAvatar = () => {
    setSelectedAvatar(null);
    setAvatarPreview(null);
    setAvatarError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input click
  const handleAvatarClick = () => {
    setAvatarError(null); // Clear previous errors when opening file picker
    fileInputRef.current?.click();
  };

  const handleSubmit = async (values: any) => {
    setIsUpdating(true);
    try {
      // Check if there are profile changes
      const hasProfileChanges =
        values.name !== user?.name ||
        values.tg_tag !== user?.tg_tag ||
        values.whats_app !== user?.whats_app;

      // Update profile data only if there are changes
      if (hasProfileChanges) {
        await clientAuthService.updateProfile(values);
      }

      // Update avatar if selected
      if (selectedAvatar) {
        try {
          setIsAvatarUploading(true);
          const formData = new FormData();
          formData.append('file', selectedAvatar);
          await clientAuthService.updateProfileAvatar(formData);
          setAvatarError(null); // Clear avatar error on success
        } catch (avatarError: any) {
          console.error('Ошибка при загрузке аватара:', avatarError);
          setAvatarError(avatarError?.response?.data?.detail || t('settings.avatarUploadError'));
          setIsAvatarUploading(false);
          return; // Don't refresh user if avatar upload failed
        } finally {
          setIsAvatarUploading(false);
        }
      }

      // Only refresh user if there were actual changes
      if (hasProfileChanges || selectedAvatar) {
        await refreshUser();

        // Reset avatar states after successful update
        setSelectedAvatar(null);
        setAvatarPreview(null);
        setAvatarError(null);
        setError(null); // Clear profile error on success
      }
    } catch (error: any) {
      console.error('Ошибка при обновлении учетных данных:', error);
      setError(error?.response?.data?.detail || t('settings.updateError'));
    } finally {
      setIsUpdating(false);
    }
  };
  const handleSubmitPassword = async (values: any) => {
    setIsUpdatingPassword(true);
    try {
      await clientAuthService.changePasswordSettings({ password: values?.password });
      showCreatorSuccess(t('settings.updatePasswordSuccess'));
      await refreshUser();
    } catch (error: any) {
      showError(t('settings.updatePasswordError'));
      setError(error?.response?.data?.detail || t('settings.updateError'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };
  const toggleVisibility = () => setIsVisible(!isVisible);
  const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);
  return (
    <div className="flex flex-col gap-6 md:gap-4 p-4 py-6 md:px-6 md:py-4">
      <Typography className="md:py-4" variant="h5">
        {t('settings.accountSettings')}
      </Typography>
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-12">
        <div className="flex w-full min-w-0 max-w-[12.5rem] flex-col gap-4 max-md:mx-auto max-md:items-center md:shrink-0">
          <div className="relative w-full max-w-[12.5rem]">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Avatar preview"
                className="aspect-square w-full max-h-[12.5rem] rounded-3 object-cover"
              />
            ) : user?.avatar ? (
              <img
                src={user.avatar}
                alt="User avatar"
                className="aspect-square w-full max-h-[12.5rem] rounded-3 object-cover"
              />
            ) : (
              <div className="flex aspect-square w-full max-h-[12.5rem] max-w-full items-center justify-center overflow-hidden rounded-3 [&_svg]:h-full [&_svg]:w-full [&_svg]:max-h-full">
                <NoAvatarBigIc />
              </div>
            )}
            <div
              className="absolute top-[-1rem] right-[-1rem] cursor-pointer"
              onClick={handleAvatarClick}
            >
              <EditBlueRoundedIc />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleAvatarSelect}
              className="hidden"
            />
          </div>
          {avatarPreview && (
            <Button
              onClick={handleCancelAvatar}
              className="text-auth-strength-weak bg-auth-strength-weak/20 text-sm leading-5 transition-colors"
            >
              {t('settings.cancelSelection')}
            </Button>
          )}
          <Typography className="text-auth-muted max-md:text-center" variant="body2">
            {t('settings.allowedFileTypes')}
          </Typography>
          {avatarError && (
            <div className="flex items-start gap-1">
              <InfoRedIc size={18} />
              <Typography className="text-danger" variant="body2">
                {avatarError}
              </Typography>
            </div>
          )}
        </div>
        <div className="flex min-w-0 w-full flex-col gap-6 md:max-w-[33.125rem] md:flex-1">
          <Formik
            initialValues={{
              email: user?.email,
              name: user?.name,
              tg_tag: user?.tg_tag,
              whats_app: user?.whats_app,
              type: user?.type,
            }}
            //@ts-ignore
            validationSchema={createSignUpSchemaPatched(user?.type, t)}
            onSubmit={(values, actions) => {
              handleSubmit(values);
              actions.setSubmitting(false);
            }}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isValid,
              isSubmitting,
            }) => {
              // Check if there are any changes in profile data
              const hasProfileChanges =
                values.name !== user?.name ||
                values.tg_tag !== user?.tg_tag ||
                values.whats_app !== user?.whats_app;

              // Check if there are any changes (profile or avatar)
              const hasAnyChanges = hasProfileChanges || selectedAvatar;

              // Check if form is valid and has changes
              const canSubmit = hasAnyChanges && !isUpdating && !isAvatarUploading;

              return (
                <Form onSubmit={handleSubmit}>
                  <div className="flex flex-col gap-4 items-start">
                    <Typography variant="h6">{user?.name}</Typography>
                    <Card className="w-full rounded-3.5 border border-gray-90 shadow-sm">
                      <CardBody className="p-4">
                        <div className="flex flex-col gap-4">
                          <div className="opacity-50 pointer-events-none [&_.cursor-pointer]:pointer-events-auto">
                            <CustomHeroInput
                              label={t('settings.accountType')}
                              infoIconText={t('settings.accountTypeTooltip')}
                              name="type"
                              value={values.type}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.type}
                              touched={touched.type}
                            />
                          </div>
                          <div className="opacity-50 pointer-events-none [&_.cursor-pointer]:pointer-events-auto">
                            <CustomHeroInput
                              label={t('settings.email')}
                              infoIconText={t('settings.emailTooltip')}
                              name="email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.email}
                              touched={touched.email}
                            />
                          </div>
                          <CustomHeroInput
                            label={
                              user?.type === 'agency'
                                ? t('settings.agencyName')
                                : t('settings.name')
                            }
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            isRequired
                            onBlur={handleBlur}
                            error={errors.name}
                            touched={touched.name}
                          />
                          <CustomHeroInput
                            label={t('settings.telegram')}
                            name="tg_tag"
                            value={values.tg_tag}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            error={errors.tg_tag}
                            touched={touched.tg_tag}
                          />
                          <CustomHeroInput
                            label={t('settings.otherContacts')}
                            name="whats_app"
                            value={values.whats_app}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.whats_app}
                            touched={touched.whats_app}
                          />
                          <Typography variant="body3" className="text-auth-muted">
                            {t('settings.contactsNeeded')}
                          </Typography>
                        </div>
                      </CardBody>
                    </Card>
                    <CustomHeroButton
                      className="mt-2"
                      //@ts-ignore
                      type="submit"
                      disabled={!canSubmit}
                      isLoading={isUpdating || isAvatarUploading}
                      title={
                        isUpdating
                          ? t('settings.updating')
                          : isAvatarUploading
                            ? t('settings.uploadingAvatar')
                            : t('settings.saveChanges')
                      }
                      onPress={handleSubmit}
                    />
                    {error && (
                      <AlertBlock
                        data={[
                          {
                            severity: 'error',
                            title: t('settings.error'),
                            description: error,
                          },
                        ]}
                        type="rounded"
                      />
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
          <Formik
            initialValues={{
              password: '',
              confirmPassword: '',
            }}
            //@ts-ignore
            validationSchema={changePasswordSchema(t)}
            onSubmit={(values, actions) => {
              handleSubmitPassword(values);
              actions.setSubmitting(false);
            }}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleBlur,
              handleSubmit,
              isValid,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="flex flex-col items-start gap-4">
                  <Typography variant="h6">{t('settings.changePassword')}</Typography>
                  <Card className="w-full rounded-3.5 border border-gray-90 shadow-sm">
                    <CardBody className="p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col w-full">
                          <CustomHeroInput
                            label={t('settings.password')}
                            endContent={
                              <button type="button" onClick={toggleVisibility}>
                                {isVisible ? (
                                  <Icon
                                    className="pointer-events-none text-2xl w-5 text-default-400"
                                    icon="solar:eye-closed-linear"
                                  />
                                ) : (
                                  <Icon
                                    className="pointer-events-none text-2xl w-5 text-default-400"
                                    icon="solar:eye-bold"
                                  />
                                )}
                              </button>
                            }
                            name="password"
                            placeholder={t('settings.enterPassword')}
                            type={isVisible ? 'text' : 'password'}
                            value={values.password}
                            onChange={(e) => setFieldValue('password', e.target.value)}
                            error={errors.password}
                            touched={touched.password}
                          />
                          {/* Password Strength Indicator */}
                          <div className="flex gap-1 w-full mt-2">
                            {[1, 2, 3, 4].map((level) => {
                              const currentStrength = calculatePasswordStrength(values.password);
                              const isActive = currentStrength >= level;
                              const strengthColor = getPasswordStrengthColor(currentStrength);
                              const color = isActive ? strengthColor : '#d4d4d8';

                              return (
                                <div
                                  key={level}
                                  className="h-1.5 flex-1 rounded-3.5 password-strength-bar"
                                  style={{ backgroundColor: color }}
                                />
                              );
                            })}
                          </div>

                          <p className="text-sm text-auth-muted leading-5 mt-1">
                            {t('settings.passwordHint')}
                          </p>
                        </div>

                        {/* Confirm Password Field */}
                        <CustomHeroInput
                          label={t('settings.repeatPassword')}
                          endContent={
                            <button type="button" onClick={toggleConfirmVisibility}>
                              {isConfirmVisible ? (
                                <Icon
                                  className="pointer-events-none text-2xl w-5 text-default-400"
                                  icon="solar:eye-closed-linear"
                                />
                              ) : (
                                <Icon
                                  className="pointer-events-none text-2xl w-5 text-default-400"
                                  icon="solar:eye-bold"
                                />
                              )}
                            </button>
                          }
                          name="confirmPassword"
                          placeholder={t('settings.enterPasswordAgain')}
                          type={isConfirmVisible ? 'text' : 'password'}
                          value={values.confirmPassword}
                          onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                          error={errors.confirmPassword}
                          touched={touched.confirmPassword}
                        />
                      </div>
                    </CardBody>
                  </Card>
                  <CustomHeroButton
                    className="mt-2"
                    //@ts-ignore
                    type="submit"
                    disabled={isUpdatingPassword || !isValid}
                    isLoading={isUpdatingPassword}
                    title={isUpdatingPassword ? t('settings.updating') : t('settings.savePassword')}
                    onPress={handleSubmit}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Settings;
